import { useState } from "react";
import { ConfirmModal } from "@a2z/ui";
import { useTranslate } from "@/lib/i18n";

/**
 * The dismissal guard, and the one prompt it raises (UX-round D11).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * WHY THIS EXISTS AT ALL
 * ----------------------
 * `ModalSheet` gives every dialog in this app Escape, a backdrop click and — on
 * a phone — **swipe-down-to-dismiss with live drag tracking**. Twelve POS input
 * modals passed `onClose={onCancel}` straight through, so a thumb brushing down
 * the sheet while walking discarded whatever had been typed, silently, with no
 * way back. Four back-office surfaces guarded; the twelve used at speed, in a
 * dark room, did not.
 *
 * WHY IT IS A CALL-SITE HOOK AND NOT A CHANGE TO `ModalSheet` (ruling D6 / R11)
 * ---------------------------------------------------------------------------
 * `ModalSheet`'s dismissal behaviour is a **documented contract clause** that
 * four portals rely on. Making the primitive ask before closing would change
 * every dialog in four front-ends from one edit nobody reviewed. So the guard
 * lives where the dirtiness is knowable — at the call site — and the primitive
 * is untouched. `RoleEditorDrawer` had already worked this out by hand; this is
 * that same shape, extracted so twelve more sheets do not each re-derive it.
 *
 * WHY `ConfirmModal` AND NOT `@a2z/ui`'s `DiscardChangesDialog`
 * ------------------------------------------------------------
 * `DiscardChangesDialog` wraps `ConfirmDialog`, the OLDER confirm primitive.
 * The restaurant standardised on `ConfirmModal` (UX-round D12: "one confirm primitive in
 * the product"), and all four of its existing unsaved-changes takes already use
 * it. Adopting `DiscardChangesDialog` here would move a restaurant dialog DOWN
 * onto the primitive the product is leaving, and re-basing that shared wrapper
 * onto `ConfirmModal` would re-skin seven untested seller call sites from a
 * restaurant UX round. Neither is this slice's to do — recorded rather than
 * half-done.
 */

/**
 * The prompt itself.
 *
 * `elevated` by default: it usually stacks on top of the sheet it is asking
 * about, and a second full-strength backdrop would compound to near-black.
 *
 * `tone="danger"`, deliberately: the three-question test asks whether the same
 * person can undo the act from the surface they are standing on, and they
 * cannot — the typed text is gone the moment the sheet closes. There is no
 * caution rung for "gone", only for "reaches past this screen and comes back".
 */
export function UnsavedChangesPrompt({
  isOpen,
  onDiscard,
  onKeepEditing,
  elevated = true,
  testId = "unsaved-changes-prompt",
}: {
  isOpen: boolean;
  onDiscard: () => void;
  onKeepEditing: () => void;
  /**
   * Defaults to TRUE because almost every caller is a dialog asking about
   * itself, and a second full-strength backdrop over the sheet compounds to
   * near-black. Pass `false` for a PAGE-level guard — a route blocker or a
   * selection change — where there is no sheet underneath to see through.
   */
  elevated?: boolean;
  testId?: string;
}) {
  const t = useTranslate();
  return (
    <ConfirmModal
      isOpen={isOpen}
      elevated={elevated}
      tone="danger"
      onClose={onKeepEditing}
      onConfirm={onDiscard}
      title={t((m) => m.common.unsavedTitle)}
      description={t((m) => m.common.unsavedBody)}
      confirmLabel={t((m) => m.common.discardChanges)}
      cancelLabel={t((m) => m.common.keepEditing)}
      closeLabel={t((m) => m.common.close)}
      testId={testId}
    />
  );
}

export interface DiscardGuard {
  /** Is the prompt up? Pass to `UnsavedChangesPrompt`'s `isOpen`. */
  asking: boolean;
  /** Wire to the sheet's `onClose` — Escape, backdrop and swipe all land here. */
  requestClose: () => void;
  /** The prompt's safe action. */
  keepEditing: () => void;
  /** The prompt's destructive action. */
  discard: () => void;
}

/**
 * Route one sheet's dismissal through a dirtiness check.
 *
 * `isDirty` is the CALL SITE's question, because only it knows what "typed
 * work" means on its own sheet: text in a note, a ticked plate, a tender that
 * is not the total. A sheet whose user has touched nothing closes on a swipe
 * exactly as it does today — the guard must not tax the ordinary dismissal.
 *
 * The `asking` re-entrancy check is not optional. Both sheets listen for
 * Escape, so without it a single keypress closes the prompt and immediately
 * re-opens it, and the sheet can never be left by keyboard at all.
 */
export function useDiscardGuard(
  isDirty: boolean,
  onLeave: () => void,
): DiscardGuard {
  const [asking, setAsking] = useState(false);
  return {
    asking,
    requestClose: () => {
      if (asking) return;
      if (isDirty) {
        setAsking(true);
        return;
      }
      onLeave();
    },
    keepEditing: () => setAsking(false),
    discard: () => {
      setAsking(false);
      onLeave();
    },
  };
}
